import React, { useEffect, useRef, useState, useCallback } from 'react';
import { canvasToBlob } from '../../lib/ImageConversion';

const COLORS = [
  { value: '#000000', label: 'Black' },
  { value: '#ffffff', label: 'White' },
  { value: '#ff0000', label: 'Red' },
  { value: '#00a82d', label: 'Green' },
  { value: '#1a73e8', label: 'Blue' },
];

const SIZES = [
  { value: 4, label: 'Thin' },
  { value: 8, label: 'Medium' },
  { value: 16, label: 'Thick' },
];

const colorToBgClass = (hex) => {
  switch (hex) {
    case '#000000':
      return 'bg-black';
    case '#ffffff':
      return 'bg-white';
    case '#ff0000':
      return 'bg-red-500';
    case '#00a82d':
      return 'bg-green-600';
    case '#1a73e8':
      return 'bg-blue-600';
    default:
      return 'bg-gray-500';
  }
};

const sizeToInnerDotClass = (size) => {
  switch (size) {
    case 4:
      return 'w-2 h-2';
    case 8:
      return 'w-3.5 h-3.5';
    case 16:
      return 'w-5 h-5';
    default:
      return 'w-3.5 h-3.5';
  }
};

const ImageEditor = ({
  src,
  onSave,
  onCancel,
  isOpen = true,
}) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const [selectedColor, setSelectedColor] = useState(COLORS[0].value);
  const [selectedSize, setSelectedSize] = useState(SIZES[1].value);
  const [imageLoaded, setImageLoaded] = useState(false);

  const loadAndDrawImage = useCallback(() => {
    console.log('loadAndDrawImage', src);
    if (!src) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const context = canvas.getContext('2d');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(img, 0, 0);
      setImageLoaded(true);
    };
    img.onerror = () => {
      setImageLoaded(false);
    };
    img.src = src;
  }, [src]);

  useEffect(() => {
    if (isOpen) {
      loadAndDrawImage();
    }
  }, [isOpen, loadAndDrawImage]);

  const getCanvasScale = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return { scaleX: 1, scaleY: 1, rect: { left: 0, top: 0 } };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { scaleX, scaleY, rect };
  }, []);

  const toCanvasPoint = useCallback((clientX, clientY) => {
    const { scaleX, scaleY, rect } = getCanvasScale();
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }, [getCanvasScale]);

  const beginStroke = useCallback((x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    isDrawingRef.current = true;
    lastPointRef.current = { x, y };
  }, []);

  const drawStroke = useCallback((x, y) => {
    const canvas = canvasRef.current;
    if (!canvas || !isDrawingRef.current) return;
    const context = canvas.getContext('2d');
    context.strokeStyle = selectedColor;
    context.lineWidth = selectedSize;
    context.lineJoin = 'round';
    context.lineCap = 'round';

    const { x: lastX, y: lastY } = lastPointRef.current;
    context.beginPath();
    context.moveTo(lastX, lastY);
    context.lineTo(x, y);
    context.stroke();
    lastPointRef.current = { x, y };
  }, [selectedColor, selectedSize]);

  const endStroke = useCallback(() => {
    isDrawingRef.current = false;
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (!imageLoaded) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture?.(e.pointerId);
    const point = toCanvasPoint(e.clientX, e.clientY);
    beginStroke(point.x, point.y);
    e.preventDefault();
  }, [beginStroke, toCanvasPoint, imageLoaded]);

  const handlePointerMove = useCallback((e) => {
    if (!isDrawingRef.current) return;
    const point = toCanvasPoint(e.clientX, e.clientY);
    drawStroke(point.x, point.y);
    e.preventDefault();
  }, [drawStroke, toCanvasPoint]);

  const handlePointerUp = useCallback((e) => {
    if (!isDrawingRef.current) return;
    endStroke();
    e.preventDefault();
  }, [endStroke]);

  const handleApply = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !onSave) return;
    
    try {
      const blob = await canvasToBlob(canvas, 'image/png', 1);
      onSave(blob);
    } catch (error) {
      console.error('Failed to convert canvas to blob:', error);
    }
  }, [onSave]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/75 flex flex-col">
      <div className="flex-1 flex items-center justify-center overflow-hidden p-4 m-0">
        <canvas
          ref={canvasRef}
          className="max-w-full max-h-full touch-none cursor-crosshair ring-1 ring-black/10 dark:ring-white/10 bg-transparent"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>

      <div className="w-full min-h-[72px] flex items-center justify-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap justify-center">

          <div className="flex items-center gap-2" role="group" aria-label="Brush color">
            {COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                aria-label={`${c.label} color`}
                title={c.label}
                onClick={() => setSelectedColor(c.value)}
                className={
                  `w-6 h-6 rounded-full border ${colorToBgClass(c.value)} ` +
                  `${selectedColor === c.value
                    ? 'ring-2 ring-gray-900 dark:ring-gray-100 scale-110'
                    : 'border-slate-300 dark:border-gray-700 hover:ring-2 hover:ring-gray-300 dark:hover:ring-gray-600'} ` +
                  'transition transform'
                }
              />
            ))}
          </div>

          <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />

          <div className="flex items-center gap-2" role="group" aria-label="Brush size">
            {SIZES.map((s) => (
              <button
                key={s.value}
                type="button"
                aria-label={`${s.label} size`}
                title={`${s.label}`}
                onClick={() => setSelectedSize(s.value)}
                className={
                  `w-8 h-8 rounded-full flex items-center justify-center ` +
                  `${selectedSize === s.value
                    ? 'ring-2 ring-blue-500 dark:ring-blue-400 shadow-md shadow-blue-300/40 scale-110 bg-white dark:bg-gray-900'
                    : 'ring-1 ring-gray-300 dark:ring-gray-700 bg-white dark:bg-gray-900 hover:ring-gray-400 dark:hover:ring-gray-600'} ` +
                  'transition transform'
                }
              >
                <span className={`rounded-full bg-gray-900 dark:bg-gray-100 ${sizeToInnerDotClass(s.value)}`} />
              </button>
            ))}
          </div>
        </div>

        <div className="w-px h-8 bg-gray-300 dark:bg-gray-700 mx-4 sm:mx-6" />

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!imageLoaded}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
